import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { usePremiumPricing } from "../useHook";
import PricingModal from "../../../Components/Modals/Pricing/PricingModal";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

function PremiumPricing() {
  const {
    getPremiumPricing,
    addPremiumPricing,
    updatePremiumPricing,
    deletePremiumPricing,
  } = usePremiumPricing();
  const { hasPermission } = usePermission();

  const canCreate = hasPermission(SystemPermissions.PremiumPricing.CREATE);
  const canUpdate = hasPermission(SystemPermissions.PremiumPricing.UPDATE);
  const canDelete = hasPermission(SystemPermissions.PremiumPricing.DELETE);

  const [pricingList, setPricingList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const openModalForAdd = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (pricingItem) => {
    setEditData(pricingItem);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const fetchPricing = async () => {
    await getPremiumPricing(setPricingList);
  };

  useEffect(() => {
    fetchPricing();
    // eslint-disable-next-line
  }, []);

  const handleSubmitPricing = async (formData) => {
    try {
      if (formData.id) {
        const response = await updatePremiumPricing(formData.id, formData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Premium Pricing Updated",
            text: "The premium pricing record has been updated successfully!",
          });
        }
      } else {
        const response = await addPremiumPricing(formData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Premium Pricing Added",
            text: "The new premium pricing record has been added successfully!",
          });
        }
      }
      fetchPricing();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.message ||
          ((formData.id
            ? "Failed to update premium pricing."
            : "Failed to add premium pricing.") + " Please try again."),
      });
      console.error("Failed to submit premium pricing:", error);
    } finally {
      closeModal();
    }
  };

  const handleDelete = async (id) => {
    const confirmResult = await Swal.fire({
      icon: "warning",
      title: "Delete Premium Pricing",
      text: "Are you sure you want to delete this premium pricing record?",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (confirmResult.isConfirmed) {
      try {
        await deletePremiumPricing(id);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The premium pricing record has been deleted.",
        });
        fetchPricing();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error?.message ||
            "Failed to delete premium pricing record. Please try again.",
        });
      }
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Premium Pricing</h2>

        {canCreate && (
          <button onClick={openModalForAdd} className="btn btn-primary h-[44px]">
            <FiPlus className="text-white" />
            Add Premium Pricing
          </button>
        )}
      </div>

      {/* Pricing Table */}
      <div className="bg-white w-full rounded-lg pagination-shadow my-5">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No</th>
                <th className="table-th">Plot Size</th>
                <th className="table-th">Layout</th>
                <th className="table-th">Structure</th>
                <th className="table-th">Interior</th>
                <th className="table-th">BoQs</th>
                {(canUpdate || canDelete) && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {pricingList?.map((item, index) => (
                <tr
                  key={item.id}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                  } hover:bg-gray-200 transition-colors duration-300`}
                >
                  <td className="table-td">{index + 1}</td>
                  <td className="table-td">{item.plotSize}</td>
                  <td className="table-td">{item.layout}</td>
                  <td className="table-td">{item.structure}</td>
                  <td className="table-td">{item.interiorDesign}</td>
                  <td className="table-td">{item.boQs}</td>
                  {(canUpdate || canDelete) && (
                    <td className="table-td flex space-x-2">
                      {canUpdate && (
                        <button
                          className="btn btn-edit text-white"
                          onClick={() => openModalForEdit(item)}
                        >
                          <FiEdit2 className="text-white" /> Edit
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn btn-delete text-white"
                          onClick={() => handleDelete(item.id)}
                        >
                          <MdDelete className="text-white" /> Delete
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}

              {pricingList.length === 0 && (
                <tr>
                  <td colSpan={canUpdate || canDelete ? 7 : 6} className="text-center py-4 text-gray-500">
                    No premium pricing records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Add/Edit Premium Pricing */}
      <PricingModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSubmitPricing}
        editData={editData}
        titlePrefix="Premium "
      />
    </>
  );
}

export default PremiumPricing;
